import { startCase } from 'lodash';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { Component, Input, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';

import { SnIncidentsAPI, SnIntegrationsAPI } from '@lib/api/services';
import { snZoomIn } from '@lib/common/animations';
import { SentryVaultComponent } from '@lib/vault';

import { LprReadsAPI, SnLprRead, SnLprVigilantRead, SnPlate } from '../../api';
import { LprPlateImageDialogComponent, SnPlateFormDialogComponent } from '../../components';

@Component({
  selector: 'sn-plate-card',
  templateUrl: './plate-card.component.html',
  styleUrls: ['./plate-card.component.scss'],
  animations: [snZoomIn],
})
export class PlateCardComponent implements OnInit, OnDestroy {
  @Input() public plate: any;
  @Input() public fields: any;
  public alarmIcon: string = null;

  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _integrationsAPI: SnIntegrationsAPI,
    private readonly _dialog: MatDialog,
    private readonly _incidentsAPI: SnIncidentsAPI,
    private readonly _lprReadsAPI: LprReadsAPI,
    private readonly _sani: DomSanitizer,
    private readonly _cd: ChangeDetectorRef,
    private readonly _router: Router,
  ) {}

  public ngOnInit(): void {
    this.getPlateData(this.plate);
    this.determineCameraType();
    if (this.plate.integratedLprType === 'vigilant') {
      this._lprReadsAPI
        .getVigilantPlateImage(this.plate.plateImageKey)
        .pipe(takeUntil(this._destroy$))
        .subscribe((blob) => {
          this.plate.plateImageSrc = this._sani.bypassSecurityTrustResourceUrl(
            URL.createObjectURL(blob),
          );
          this._cd.detectChanges();
        });

      this._lprReadsAPI
        .getVigilantPlateImage(this.plate.overviewImageKey)
        .pipe(takeUntil(this._destroy$))
        .subscribe((blob) => {
          this.plate.overviewImageSrc = this._sani.bypassSecurityTrustResourceUrl(
            URL.createObjectURL(blob),
          );
          this._cd.detectChanges();
        });
    }
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public getPlateData(plate: SnLprRead | SnLprVigilantRead): void {
    if (!plate.details) {
      return;
    }
    if (plate.details['Plate Analytics']) {
      plate.details['Plate Analytics'].forEach((detail) => {
        const key = detail.Key;
        const value = detail.Value;

        plate.details[key] = value;
      });
    }
  }

  public toStartCase(text: string): string {
    return startCase(text);
  }

  public sendToVault(plate: SnPlate): void {
    const dialog = this._dialog.open(SentryVaultComponent);
    dialog
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((vaultID) => {
        const vault = vaultID.id || vaultID;
        if (vault) {
          this._addToVault(vault, plate.id);
        }
      });
  }

  public determineCameraType(): void {
    switch (this.plate.type) {
      case 'FS ALPR':
        this.alarmIcon = 'Icon_FS';
        break;
      case 'EPIC IO LPR':
        this.alarmIcon = 'EPIC-IO';
        break;
      case 'REKOR ALPR':
        this.alarmIcon = 'Icon_Rekor';
        break;
      case 'VIGILANT LEARN ALPR':
        this.alarmIcon = 'Icon_Vigilant';
        break;
      case 'DroneSense':
        this.alarmIcon = 'Icon_DroneSense';
        break;
      case 'GENETEC ALPR':
      case 'GENETEC SHARPV ALPR':
      case 'GENETEC SHARP3 ALPR':
        this.alarmIcon = 'Icon_Genetec';
        break;
      default:
        this.alarmIcon = null;
        break;
    }
  }

  public viewHistory(plate: SnPlate): void {
    this._cd.detectChanges();
    this._dialog
      .open(SnPlateFormDialogComponent, {
        data: {
          fields: this.fields,
          formModel: {
            plate: plate.plate,
          },
        },
        width: '500px',
        disableClose: true,
      })
      .afterClosed()
      .pipe(
        filter((params: any) => params),
        takeUntil(this._destroy$),
      )
      .subscribe((params) => {
        Object.keys(params).forEach((key) => {
          if (params[key] === null || params[key] === undefined) {
            delete params[key];
          }
        });
        this._router.navigate([], {
          queryParams: params,
        });
      });
  }

  public openEnlargedImageDialog(): void {
    this._dialog.open(LprPlateImageDialogComponent, {
      data: {
        plate: this.plate,
      },
      maxWidth: '800px',
      maxHeight: '90vh',
      panelClass: 'lpr-plate__dialog-container',
    });
  }

  private _addToVault(vaultID: string, lprHitId: string): void {
    this._incidentsAPI.addLprToVault(vaultID, lprHitId).pipe(takeUntil(this._destroy$)).subscribe();
  }
}
